import React from 'react';
import TradingCardWhen from '../TradingCardWhen';
import MobileSegmentTabs, { useActionSegment } from '../MobileSegmentTabs';
import './DegenActionScreen.css';

function needPctLabel(p, targetPct, isCnc) {
  const tgt = Number(targetPct) || (isCnc ? 1 : 0.45);
  const move = Number(p.movePct);
  const need = p.needPct != null && Number.isFinite(Number(p.needPct))
    ? Number(p.needPct)
    : Math.round((tgt - (Number.isFinite(move) ? move : 0)) * 100) / 100;
  if (isCnc) {
    const holdDays = p.holdDays ?? 0;
    const lossNote = p.inLoss || p.belowSoftStop ? ' · in loss — HOLD only' : '';
    return `${holdDays}d hold · need +${need}%${lossNote}`;
  }
  return `${p.minutesLeft ?? 0} min left · need +${need}%`;
}

/**
 * Shared BUY / HOLD / SELL action screen for Degen intraday and CNC dashboards.
 */
export default function DegenActionScreen({
  guide,
  marketOpen,
  sellAlertsOpen = true,
  mode = 'intraday',
  title,
  onConfirmSold,
  confirmSoldBusy
}) {
  const isCnc = mode === 'cnc';
  const ordersOk = marketOpen && sellAlertsOpen !== false;
  const step = guide?.activeStep || 'wait';
  const stepLabel = step === 'sell'
    ? 'SELL NOW on Groww'
    : step === 'buy'
      ? (isCnc ? 'CNC BUY on Groww' : 'BUY / SHORT on Groww')
      : step === 'hold'
        ? 'HOLD — watch timer'
        : 'Scanning…';
  const buy = guide?.buy;
  const sell = guide?.sell || [];
  const hold = guide?.hold || [];
  const deliveryHoldCount = guide?.deliveryHoldCount ?? 0;
  const buyBlockReason = guide?.buyBlockReason;
  const tgt = guide?.profitTargetPct ?? (isCnc ? 1 : 0.45);
  const stop = guide?.stopPct ?? (isCnc ? 0 : 0.35);
  const maxMin = guide?.maxHoldMinutes ?? (isCnc ? null : 10);

  const [mobileTab, setMobileTab] = useActionSegment(step, sell.length > 0);
  const mobileTabs = [
    { id: 'buy', label: isCnc ? 'Buy' : 'Buy', count: buy ? 1 : 0 },
    { id: 'hold', label: 'Hold', count: hold.length },
    { id: 'sell', label: 'Sell', count: sell.length, urgent: sell.some((s) => s.profitHit || s.urgent) }
  ];

  return (
    <section className="degen-card cnc-action-screen mobile-app-panel">
      <header className="cnc-action-head">
        <div>
          <h2>{title || (isCnc ? 'Your CNC action screen' : 'Your intraday action screen')}</h2>
          <p>
            {!marketOpen
              ? 'Market closed — holds kept until next session'
              : !ordersOk
                ? isCnc
                  ? 'After 3:15 PM IST — no sell alerts or new CNC buys · delivery positions kept'
                  : 'After 3:15 PM IST — MIS square-off · no new intraday orders'
                : isCnc
                  ? `CNC delivery · Sell at +${tgt}% only · no stop loss — loss positions stay on HOLD`
                  : `Green scan row = entry · LONG BUY or SHORT · Target +${tgt}% · stop −${stop}% · ${maxMin} min rotate`}
          </p>
        </div>
        <div className={`cnc-action-pill step-${step}`}>
          <span>Do this now</span>
          <strong>{stepLabel}</strong>
        </div>
      </header>

      <MobileSegmentTabs tabs={mobileTabs} value={mobileTab} onChange={setMobileTab} />

      <div className={`cnc-action-cols mobile-tab-${mobileTab}`}>
        <div className={`cnc-action-col buy ${step === 'buy' ? 'active' : ''}${mobileTab === 'buy' ? ' mobile-show' : ''}`}>
          <h3>{isCnc ? '🟢 CNC BUY' : '🟢 BUY / SHORT'}</h3>
          {buy ? (
            <div className="cnc-action-card cnc-action-card-buy">
              <div className="cnc-action-card-top">
                <strong>{buy.stock}</strong>
                <span className={`degen-exch degen-exch-${(buy.exchange || 'NSE').toLowerCase()}`}>{buy.exchange}</span>
              </div>
              {!isCnc && buy.side && (
                <span className={`degen-side-badge ${buy.side === 'SHORT' ? 'short' : 'long'}`}>{buy.entryType || buy.side}</span>
              )}
              <hr className="cnc-action-divider" aria-hidden="true" />
              <TradingCardWhen item={buy} prefix="Signal" />
              <p>×{buy.qty} @ ₹{Number(buy.price).toFixed(2)}</p>
              <p className="cnc-action-target">
                Exit target: ₹{buy.targetPrice.toFixed(2)} ({buy.side === 'SHORT' ? '↓' : '+'}{buy.targetPct}%)
              </p>
              <p className="cnc-action-hint">
                Score {buy.score} · Δ{buy.changePct}%
                {isCnc ? ' · CNC delivery' : ` · ${maxMin} min clock`}
              </p>
              <p className="cnc-action-instruction">{buy.instruction}</p>
            </div>
          ) : (
            <p className="cnc-action-empty">
              {isCnc && buyBlockReason
                ? buyBlockReason
                : isCnc
                  ? 'No CNC buy signal — wait for pipeline or green row in scan'
                  : 'No entry signal — wait for pipeline FILL or green row (UP=BUY, DOWN=SHORT)'}
            </p>
          )}
        </div>

        <div className={`cnc-action-col hold ${step === 'hold' ? 'active' : ''}${mobileTab === 'hold' ? ' mobile-show' : ''}`}>
          <h3>🟡 HOLD</h3>
          {isCnc && deliveryHoldCount > 0 && (
            <p className="cnc-action-delivery-note">
              {deliveryHoldCount} delivery hold{deliveryHoldCount === 1 ? '' : 's'} kept below — only active slots shown here
            </p>
          )}
          {hold.length === 0 ? (
            <p className="cnc-action-empty">No open positions to hold</p>
          ) : (
            hold.map((p) => (
              <div key={p.symbol || p.stock} className={`cnc-action-card cnc-action-card-hold ${p.belowSoftStop ? 'soft-stop' : ''}`}>
                <div className="cnc-action-card-top">
                  <strong>{p.stock}</strong>
                  <span>{p.exchange}</span>
                </div>
                {!isCnc && <span className={`degen-side-badge ${p.side === 'SHORT' ? 'short' : 'long'}`}>{p.side}</span>}
                <hr className="cnc-action-divider" aria-hidden="true" />
                <TradingCardWhen item={p} prefix="Opened" />
                <p>Entry ₹{p.entryPrice.toFixed(2)} → Live ₹{Number(p.currentPrice).toFixed(2)}</p>
                <p className={p.movePct >= 0 ? 'up' : 'down'}>
                  {p.movePct >= 0 ? '+' : ''}{p.movePct}% ({p.movePerShare >= 0 ? '+' : ''}₹{p.movePerShare}/sh)
                </p>
                <p className="cnc-action-target">Target @ ₹{p.targetPrice.toFixed(2)} (+{p.targetPct}%)</p>
                <p className="cnc-action-hint">{needPctLabel(p, tgt, isCnc)}</p>
              </div>
            ))
          )}
        </div>

        <div className={`cnc-action-col sell ${step === 'sell' ? 'active' : ''}${mobileTab === 'sell' ? ' mobile-show' : ''}`}>
          <h3>🔴 SELL / EXIT</h3>
          {sell.length === 0 ? (
            <p className="cnc-action-empty">
              No exit alert — target +{tgt}%
              {isCnc
                ? ' · no stop loss — loss stays on HOLD'
                : ` · stop −${stop}% · ${maxMin} min rotate`}
            </p>
          ) : (
            sell.map((p) => (
              <div key={p.symbol || p.stock} className={`cnc-action-card cnc-action-card-sell ${p.urgent ? 'urgent' : 'profit'}`}>
                <div className="cnc-action-card-top">
                  <strong>{p.stock}</strong>
                  <span>{p.exchange}</span>
                </div>
                {!isCnc && <span className={`degen-side-badge ${p.side === 'SHORT' ? 'short' : 'long'}`}>{p.side === 'SHORT' ? 'COVER' : 'SELL'}</span>}
                <hr className="cnc-action-divider" aria-hidden="true" />
                <TradingCardWhen item={p} prefix="Opened" />
                {isCnc && (
                  <p>Entry ₹{Number(p.entryPrice).toFixed(2)} → Live ₹{Number(p.currentPrice).toFixed(2)}</p>
                )}
                <p className={p.movePct >= 0 ? 'up' : 'down'}>
                  {p.movePct >= 0 ? '+' : ''}{p.movePct}% · {p.movePerShare >= 0 ? '+' : ''}₹{p.movePerShare}/sh × {p.qty}
                </p>
                <p className="cnc-action-instruction">{p.instruction}</p>
                {isCnc && p.profitHit && !p.urgent && onConfirmSold && (
                  <button
                    type="button"
                    className="cnc-sold-btn"
                    disabled={confirmSoldBusy}
                    onClick={() => onConfirmSold(p.symbol || p.stock)}
                  >
                    {confirmSoldBusy ? '…' : 'Sold ✓ — next BUY'}
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
